import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollText, Search, Eye } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { DetailDialog, DetailRow } from "@/components/ui/detail-dialog";
import { selectClass } from "@/components/common/FormShell";
import { UserName } from "@/components/common/EntityName";
import { auditApi } from "@/lib/api";
import type { AuditEntry } from "@/lib/api/types";
import { fmtRelativeTime } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

const METHODS = ["", "POST", "PUT", "PATCH", "DELETE"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Each service numbers its own codes, so only the platform-wide ones can be
 * classified here: "00" is success, 01/02/03/99 and HTTP 4xx/5xx are failures,
 * everything else is shown as-is in a neutral badge.
 */
const tone = (code: string | null): "success" | "destructive" | "outline" => {
  if (!code) return "outline";
  if (code === "00" || /^2\d\d$/.test(code)) return "success";
  if (["01", "02", "03", "99"].includes(code) || /^[45]\d\d$/.test(code)) return "destructive";
  return "outline";
};

/** Every write that reached a service, who made it and what it answered. */
export default function AuditPage() {
  const [service, setService] = useState("");
  const [method, setMethod] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);

  const services = useQuery({ queryKey: ["audit-services"], queryFn: () => auditApi.services().then((r) => r.data.data ?? []) });
  const extra = {
    service: service || undefined,
    method: method || undefined,
    // <input type="datetime-local"> has no zone; send it as local time with the browser's offset.
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
  };
  const { q, setQ, page, setPage, items, totalItems, totalPages, isFetching } = usePagedQuery<AuditEntry, typeof extra>({
    key: ["audit"],
    fetcher: auditApi.list,
    extra,
  });
  const detail = useQuery({
    queryKey: ["audit", detailId],
    queryFn: () => auditApi.get(detailId!).then((r) => r.data.data),
    enabled: !!detailId,
  });

  return (
    <>
      <PageHeader title="Audit log" subtitle={`${totalItems} recorded changes. Reads are not logged; every create, update and delete is.`} />

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
            <div className="relative min-w-64 flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search path, user id or code…" />
            </div>
            <select value={service} onChange={(e) => { setService(e.target.value); setPage(0); }} className={`${selectClass} px-3 py-2 text-sm`}>
              <option value="">All services</option>
              {(services.data ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={method} onChange={(e) => { setMethod(e.target.value); setPage(0); }} className={`${selectClass} px-3 py-2 text-sm`}>
              {METHODS.map((m) => <option key={m} value={m}>{m || "All methods"}</option>)}
            </select>
            <Input type="datetime-local" className="w-auto" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="datetime-local" className="w-auto" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
          </div>

          {items.length === 0 ? (
            <EmptyState icon={ScrollText} title={isFetching ? "Loading…" : "No entries"} description="Nothing matches these filters." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Who</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} className="cursor-pointer" onClick={() => setDetailId(e.id)}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtRelativeTime(e.createdAt)}</TableCell>
                    <TableCell className="text-sm">
                      {e.clientIdentity && UUID.test(e.clientIdentity) ? <UserName id={e.clientIdentity} /> : (e.clientIdentity ?? "—")}
                      {e.role && e.role !== "ROLE_ANONYMOUS" && <Badge variant="muted" className="ml-1.5">{e.role}</Badge>}
                    </TableCell>
                    <TableCell>
                      <span className="mr-1.5 font-mono text-[11px] font-bold text-primary">{e.method}</span>
                      <span className="font-mono text-xs">{e.path}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.serviceName}</TableCell>
                    <TableCell><Badge variant={tone(e.statusCode)}>{e.statusCode ?? "—"}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.durationMs != null ? `${e.durationMs} ms` : "—"}</TableCell>
                    <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => setDetailId(e.id)}><Eye className="size-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      <DetailDialog
        open={!!detailId}
        onClose={() => setDetailId(null)}
        icon={ScrollText}
        title={detail.data ? `${detail.data.method} ${detail.data.path}` : "Loading…"}
        subtitle={detail.data ? `${detail.data.serviceName} · ${detail.data.statusCode ?? ""} · ${new Date(detail.data.createdAt).toLocaleString()}` : undefined}
      >
        {detail.data && (
          <>
            <DetailRow label="Who" value={detail.data.clientIdentity && UUID.test(detail.data.clientIdentity) ? <UserName id={detail.data.clientIdentity} /> : (detail.data.clientIdentity ?? "—")} />
            <DetailRow label="Role" value={detail.data.role && detail.data.role !== "ROLE_ANONYMOUS" ? detail.data.role : "—"} />
            <DetailRow label="IP" value={detail.data.ip ?? "—"} />
            <DetailRow label="Request id" value={<span className="font-mono text-xs">{detail.data.requestId}</span>} />
            <DetailRow label="Duration" value={detail.data.durationMs != null ? `${detail.data.durationMs} ms` : "—"} />
            <div className="mt-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">Request (sensitive fields are masked)</p>
              <pre className="max-h-60 overflow-auto rounded-lg bg-muted p-3 text-xs">{JSON.stringify(detail.data.requestPayload ?? {}, null, 2)}</pre>
              <p className="text-xs font-semibold text-muted-foreground">Response</p>
              <pre className="max-h-40 overflow-auto rounded-lg bg-muted p-3 text-xs">{JSON.stringify(detail.data.responsePayload ?? {}, null, 2)}</pre>
            </div>
          </>
        )}
      </DetailDialog>
    </>
  );
}
